import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText, formatMoney, formatQty, formatDate } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Partner, StockRow } from '../types';

interface FormState {
  id: string | null;
  name: string;
  number: string;
  isCustomer: boolean;
  isSupplier: boolean;
  isOwner: boolean;
  email: string;
  phone: string;
  street: string;
  zip: string;
  city: string;
  note: string;
  active: boolean;
}

const emptyForm: FormState = {
  id: null,
  name: '',
  number: '',
  isCustomer: false,
  isSupplier: false,
  isOwner: false,
  email: '',
  phone: '',
  street: '',
  zip: '',
  city: '',
  note: '',
  active: true,
};

export function PartnersPage() {
  const { can } = useAuth();
  const mayEdit = can('admin', 'kaufmann', 'disponent');

  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [sp] = useSearchParams();
  const [search, setSearch] = useState(sp.get('q') ?? '');
  useEffect(() => {
    const q = sp.get('q');
    if (q !== null) setSearch(q);
  }, [sp]);
  const [role, setRole] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const [detail, setDetail] = useState<Partner | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setPartners(await api<Partner[]>('/api/partners', { query: { search, role } }));
    } catch (err) {
      setError(errorText(err));
    }
  }, [search, role]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  function edit(partner: Partner) {
    setForm({
      id: partner.id,
      name: partner.name,
      number: partner.number,
      isCustomer: partner.is_customer,
      isSupplier: partner.is_supplier,
      isOwner: partner.is_owner,
      email: partner.email ?? '',
      phone: partner.phone ?? '',
      street: partner.street ?? '',
      zip: partner.zip ?? '',
      city: partner.city ?? '',
      note: partner.note ?? '',
      active: partner.active,
    });
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        number: form.number || undefined,
        name: form.name,
        isCustomer: form.isCustomer,
        isSupplier: form.isSupplier,
        isOwner: form.isOwner,
        email: form.email || null,
        phone: form.phone || null,
        street: form.street || null,
        zip: form.zip || null,
        city: form.city || null,
        note: form.note || null,
        active: form.active,
      };
      if (form.id) {
        await api(`/api/partners/${form.id}`, { method: 'PUT', body });
      } else {
        await api('/api/partners', { method: 'POST', body });
      }
      setMessage(`Partner ${form.name} gespeichert.`);
      setForm(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {detail && !form && (
        <PartnerDetail
          partner={detail}
          mayEdit={mayEdit}
          onEdit={() => {
            edit(detail);
            setDetail(null);
          }}
          onClose={() => setDetail(null)}
        />
      )}

      {form && (
        <Card title={form.id ? 'Partner bearbeiten' : 'Neuer Partner'}>
          <div className="form-grid">
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <TextField
              label="Nummer"
              value={form.number}
              onChange={(v) => setForm({ ...form, number: v })}
              hint="leer lassen für automatische Vergabe"
            />
            <TextField label="E-Mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <TextField label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <TextField label="Strasse" value={form.street} onChange={(v) => setForm({ ...form, street: v })} />
            <TextField label="PLZ" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
            <TextField label="Ort" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <TextField label="Bemerkung" value={form.note} onChange={(v) => setForm({ ...form, note: v })} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            <CheckField label="Kunde" checked={form.isCustomer} onChange={(v) => setForm({ ...form, isCustomer: v })} />
            <CheckField label="Lieferant" checked={form.isSupplier} onChange={(v) => setForm({ ...form, isSupplier: v })} />
            <CheckField
              label="Auftraggeber (Warenbesitzer)"
              checked={form.isOwner}
              onChange={(v) => setForm({ ...form, isOwner: v })}
            />
            <CheckField label="Aktiv" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          </div>

          {form.isOwner && (
            <Notice kind="info">
              Als Auftraggeber markierte Partner können Eigentümer von Beständen sein und tauchen in
              allen Eigentümer-Auswahlen auf.
            </Notice>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={save} disabled={busy}>
              {busy ? 'Speichern …' : 'Speichern'}
            </button>
            <button onClick={() => setForm(null)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 260 }}>
          <span>Suche</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name oder Nummer" />
        </label>
        <label className="field">
          <span>Rolle</span>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">alle</option>
            <option value="customer">Kunden</option>
            <option value="supplier">Lieferanten</option>
            <option value="owner">Auftraggeber</option>
          </select>
        </label>
        <span className="spacer" />
        {mayEdit && (
          <button className="primary" onClick={() => setForm({ ...emptyForm })}>
            Neuer Partner
          </button>
        )}
      </div>

      <Card padded={false}>
        {!partners ? (
          <Loading />
        ) : partners.length === 0 ? (
          <Empty>Keine Partner gefunden.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Name</th>
                  <th>Rollen</th>
                  <th>Ort</th>
                  <th>Kontakt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={partner.id} onClick={() => setDetail(partner)} style={{ cursor: 'pointer' }}>
                    <td className="mono">{partner.number}</td>
                    <td>
                      {partner.name}
                      {!partner.active && (
                        <>
                          {' '}
                          <Badge kind="bad">inaktiv</Badge>
                        </>
                      )}
                    </td>
                    <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {partner.is_self && <Badge kind="ok">eigene Firma</Badge>}
                      {partner.is_owner && <Badge kind="info">Auftraggeber</Badge>}
                      {partner.is_customer && <Badge>Kunde</Badge>}
                      {partner.is_supplier && <Badge>Lieferant</Badge>}
                    </td>
                    <td className="small">{[partner.zip, partner.city].filter(Boolean).join(' ') || '—'}</td>
                    <td className="small">{partner.email ?? partner.phone ?? '—'}</td>
                    <td className="num">
                      {mayEdit && (
                        <button
                          className="small ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            edit(partner);
                          }}
                        >
                          bearbeiten
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

interface OrderRow {
  id: string;
  number: string;
  reference: string | null;
  status: string;
  requested_at?: string | null;
  expected_at?: string | null;
  qty_ordered?: number;
  qty_shipped?: number;
  qty_delivered?: number;
  value_cents?: number;
}

interface InvoiceRow {
  id: string;
  number: string;
  status: string;
  gross_cents: number;
  due_date: string | null;
  invoice_date?: string | null;
  period_end?: string | null;
}

interface OffenerPosten {
  id: string;
  number: string;
  open_cents: number;
  due_date: string | null;
  days_overdue: number;
}
interface Receivables {
  items: OffenerPosten[];
  summary: { openCents: number; overdueCents: number; invoices: number };
}

type PartnerTab = 'uebersicht' | 'bestand' | 'auftraege' | 'posten' | 'bestellungen' | 'eingang';

/**
 * Partner-Detailansicht mit Reitern - wie beim Artikel: der Partner ist der
 * Ort, an dem alles zu ihm zusammenläuft. Welche Reiter erscheinen, hängt an
 * seinen Rollen: der Auftraggeber bringt Bestand, Aufträge und offene Posten
 * mit, der Lieferant Bestellungen und Rechnungseingang.
 */
function PartnerDetail({
  partner,
  mayEdit,
  onEdit,
  onClose,
}: {
  partner: Partner;
  mayEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const reiter: [PartnerTab, string][] = [
    ['uebersicht', 'Übersicht'],
    ...(partner.is_owner
      ? ([
          ['bestand', 'Bestand'],
          ['auftraege', 'Aufträge'],
          ['posten', 'Offene Posten'],
        ] as [PartnerTab, string][])
      : []),
    ...(partner.is_supplier
      ? ([
          ['bestellungen', 'Bestellungen'],
          ['eingang', 'Rechnungseingang'],
        ] as [PartnerTab, string][])
      : []),
  ];

  const [tab, setTab] = useState<PartnerTab>('uebersicht');
  const [bestand, setBestand] = useState<StockRow[] | null>(null);
  const [auftraege, setAuftraege] = useState<OrderRow[] | null>(null);
  const [posten, setPosten] = useState<Receivables | null>(null);
  const [bestellungen, setBestellungen] = useState<OrderRow[] | null>(null);
  const [eingang, setEingang] = useState<InvoiceRow[] | null>(null);

  useEffect(() => {
    if (tab === 'bestand' && bestand === null) {
      api<StockRow[]>('/api/stock', { query: { ownerId: partner.id } }).then(setBestand).catch(() => setBestand([]));
    }
    if (tab === 'auftraege' && auftraege === null) {
      api<OrderRow[]>('/api/outbound/orders', { query: { ownerId: partner.id, openOnly: 'false' } })
        .then(setAuftraege)
        .catch(() => setAuftraege([]));
    }
    if (tab === 'posten' && posten === null) {
      api<Receivables>('/api/billing/receivables', { query: { ownerId: partner.id } })
        .then(setPosten)
        .catch(() => setPosten({ items: [], summary: { openCents: 0, overdueCents: 0, invoices: 0 } }));
    }
    if (tab === 'bestellungen' && bestellungen === null) {
      api<OrderRow[]>('/api/purchasing/orders', { query: { supplierId: partner.id } })
        .then(setBestellungen)
        .catch(() => setBestellungen([]));
    }
    if (tab === 'eingang' && eingang === null) {
      api<InvoiceRow[]>('/api/incoming/invoices', { query: { supplierId: partner.id } })
        .then(setEingang)
        .catch(() => setEingang([]));
    }
  }, [tab, partner.id, bestand, auftraege, posten, bestellungen, eingang]);

  return (
    <Card
      title={`${partner.number} — ${partner.name}${partner.active ? '' : '  (inaktiv)'}`}
      actions={
        <span style={{ display: 'flex', gap: 8 }}>
          {mayEdit && (
            <button className="primary" onClick={onEdit}>
              Bearbeiten
            </button>
          )}
          <button onClick={onClose}>Schließen</button>
        </span>
      }
    >
      <div className="tabs" style={{ marginBottom: 16 }}>
        {reiter.map(([wert, label]) => (
          <button key={wert} className={tab === wert ? 'active' : ''} onClick={() => setTab(wert)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'uebersicht' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Kontakt</div>
            <PZeile label="Rollen">
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {partner.is_self && <Badge kind="ok">eigene Firma</Badge>}
                {partner.is_owner && <Badge kind="info">Auftraggeber</Badge>}
                {partner.is_customer && <Badge>Kunde</Badge>}
                {partner.is_supplier && <Badge>Lieferant</Badge>}
              </span>
            </PZeile>
            <PZeile label="Anschrift">
              {[partner.street, [partner.zip, partner.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
            </PZeile>
            <PZeile label="E-Mail">{partner.email}</PZeile>
            <PZeile label="Telefon">{partner.phone}</PZeile>
            <PZeile label="Notiz">{partner.note}</PZeile>
          </div>
          <div>
            <div className="small muted" style={{ fontWeight: 600, marginBottom: 4 }}>Konditionen</div>
            <PZeile label="Debitorenkonto">{partner.debitor_account}</PZeile>
            <PZeile label="Kreditorenkonto">{partner.kreditor_account}</PZeile>
            <PZeile label="USt-IdNr.">{partner.vat_id}</PZeile>
            <PZeile label="Käuferreferenz">{partner.buyer_reference}</PZeile>
            <PZeile label="Steuerbehandlung">
              {partner.tax_treatment === 'inland'
                ? 'Inland'
                : partner.tax_treatment === 'eu_reverse_charge'
                  ? 'EU (Reverse Charge)'
                  : 'Drittland'}
            </PZeile>
          </div>
        </div>
      )}

      {tab === 'bestand' &&
        (bestand === null ? (
          <Loading />
        ) : bestand.length === 0 ? (
          <Empty>Kein Bestand für diesen Auftraggeber.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Platz</th>
                  <th>Charge</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {bestand.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <span className="mono">{r.sku}</span> {r.article_name}
                    </td>
                    <td className="mono small">{r.warehouse_code} · {r.location_code}</td>
                    <td className="small">{r.lot_code ?? '—'}</td>
                    <td className="num">{formatQty(r.qty, r.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'auftraege' &&
        (auftraege === null ? (
          <Loading />
        ) : auftraege.length === 0 ? (
          <Empty>Keine Aufträge.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Referenz</th>
                  <th>Status</th>
                  <th className="num">bestellt</th>
                  <th className="num">versendet</th>
                </tr>
              </thead>
              <tbody>
                {auftraege.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">{o.number}</td>
                    <td className="small">{o.reference ?? '—'}</td>
                    <td className="small">{o.status}</td>
                    <td className="num">{o.qty_ordered ?? '—'}</td>
                    <td className="num">{o.qty_shipped ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'posten' &&
        (posten === null ? (
          <Loading />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
              <div>
                <div className="small muted">Offen</div>
                <strong>{formatMoney(posten.summary.openCents)}</strong>
              </div>
              <div>
                <div className="small muted">davon überfällig</div>
                <strong style={{ color: posten.summary.overdueCents > 0 ? 'var(--bad)' : undefined }}>
                  {formatMoney(posten.summary.overdueCents)}
                </strong>
              </div>
            </div>
            {posten.items.length === 0 ? (
              <Empty>Keine offenen Posten.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Rechnung</th>
                      <th>fällig</th>
                      <th className="num">offen</th>
                      <th className="num">überfällig (Tage)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posten.items.map((p) => (
                      <tr key={p.id}>
                        <td className="mono">{p.number}</td>
                        <td className="small muted">{formatDate(p.due_date)}</td>
                        <td className="num">{formatMoney(p.open_cents)}</td>
                        <td className="num">{p.days_overdue > 0 ? p.days_overdue : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ))}

      {tab === 'bestellungen' &&
        (bestellungen === null ? (
          <Loading />
        ) : bestellungen.length === 0 ? (
          <Empty>Keine Bestellungen.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Status</th>
                  <th>erwartet</th>
                  <th className="num">Wert</th>
                </tr>
              </thead>
              <tbody>
                {bestellungen.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">{o.number}</td>
                    <td className="small">{o.status}</td>
                    <td className="small muted">{formatDate(o.expected_at ?? null)}</td>
                    <td className="num">{o.value_cents != null ? formatMoney(o.value_cents) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'eingang' &&
        (eingang === null ? (
          <Loading />
        ) : eingang.length === 0 ? (
          <Empty>Keine Eingangsrechnungen.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th>Datum</th>
                  <th>Status</th>
                  <th className="num">Brutto</th>
                </tr>
              </thead>
              <tbody>
                {eingang.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.number}</td>
                    <td className="small muted">{formatDate(r.invoice_date ?? null)}</td>
                    <td className="small">{r.status}</td>
                    <td className="num">{formatMoney(r.gross_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </Card>
  );
}

function PZeile({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ display: 'flex', gap: 10, padding: '3px 0' }}>
      <div className="small muted" style={{ minWidth: 140 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
